import React, { useEffect, useMemo, useState } from "react";
import api, { formatErr, LOGO_URL } from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { FileBarChart, Download, Printer } from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];
const LOGO = LOGO_URL;
const fmt = (n) => (Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MonthlyReport() {
  const today = new Date();
  const [users, setUsers] = useState([]);
  const [mitarbeiter, setMitarbeiter] = useState("");
  const [monat, setMonat] = useState(today.getMonth() + 1);
  const [jahr, setJahr] = useState(today.getFullYear());
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
  }, []);

  const generate = async () => {
    if (!mitarbeiter) { toast.error("Bitte Mitarbeiter auswählen"); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/monthly-report", { params: { mitarbeiter, monat, jahr } });
      setReport(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const [exportBusy, setExportBusy] = useState(null); // "csv" | "pdf" | null

  const exportCsv = async () => {
    if (!report) return;
    setExportBusy("csv");
    try {
      const r = await api.get("/export/monthly-report.csv", { params: { mitarbeiter, monat, jahr }, responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `monatsbericht_${mitarbeiter.replace(/\s/g, "_")}_${jahr}-${String(monat).padStart(2, "0")}.csv`;
      a.click();
    } finally { setExportBusy(null); }
  };

  const exportPdf = async () => {
    if (!report) return;
    setExportBusy("pdf");
    try {
      const r = await api.get("/export/monthly-report.pdf", { params: { mitarbeiter, monat, jahr }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `monatsbericht_${mitarbeiter.replace(/\s/g, "_")}_${jahr}-${String(monat).padStart(2, "0")}.pdf`;
      a.click();
    } finally { setExportBusy(null); }
  };

  const years = useMemo(() => {
    const ys = [];
    for (let y = today.getFullYear() + 1; y >= today.getFullYear() - 5; y--) ys.push(y);
    return ys;
  }, [today]);

  const s = report?.summary;

  return (
    <div data-testid="monthly-report">
      <PageHeader title="Monatliche Abrechnung" subtitle="Detaillierter Monatsbericht pro Mitarbeiter" />

      <div className="bg-white border rounded-md p-5 mb-5 grid grid-cols-1 md:grid-cols-4 gap-4 items-end" style={{ borderColor: "#E2E8F0" }}>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Mitarbeiter</Label>
          <Select value={mitarbeiter} onValueChange={setMitarbeiter}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="report-mitarbeiter"><SelectValue placeholder="— auswählen —" /></SelectTrigger>
            <SelectContent>
              {users.map((u) => (<SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Monat</Label>
          <Select value={String(monat)} onValueChange={(v) => setMonat(Number(v))}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="report-monat"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS.map((m, i) => (<SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Jahr</Label>
          <Select value={String(jahr)} onValueChange={(v) => setJahr(Number(v))}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="report-jahr"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={generate}
          disabled={loading || !mitarbeiter}
          className="h-12 bg-[#0A2540] hover:bg-[#113A63] text-white"
          data-testid="report-generate-btn"
        >
          <FileBarChart className="h-4 w-4 mr-2" /> {loading ? "Erstellt…" : "Bericht erstellen"}
        </Button>
      </div>

      {report && (
        <div className="print-area space-y-5">
          {/* Zusammenfassung */}
          <div className="bg-white border rounded-md p-6" style={{ borderColor: "#E2E8F0" }} data-testid="report-summary">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
              <div className="flex items-center gap-5">
                <img src={LOGO} alt="SKALARTECH" className="h-20 w-auto" data-testid="report-logo" />
                <div className="border-l pl-5" style={{ borderColor: "#E2E8F0" }}>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Monatsbericht</div>
                  <h2 className="text-2xl font-bold mt-1">{s.mitarbeiter}</h2>
                  <div className="text-sm text-slate-600">{MONTHS[s.monat - 1]} {s.jahr}</div>
                </div>
              </div>
              <div className="flex gap-2 no-print">
                <Button variant="outline" onClick={() => window.print()} className="h-10" data-testid="report-print">
                  <Printer className="h-4 w-4 mr-2" /> Drucken
                </Button>
                <Button variant="outline" onClick={exportCsv} className="h-10" disabled={exportBusy === "csv"} data-testid="report-export-csv">
                  <Download className="h-4 w-4 mr-2" /> {exportBusy === "csv" ? "Lädt…" : "Excel/CSV"}
                </Button>
                <Button onClick={exportPdf} className="h-10 bg-[#0A2540] hover:bg-[#113A63] text-white" disabled={exportBusy === "pdf"} data-testid="report-export-pdf">
                  <Download className="h-4 w-4 mr-2" /> {exportBusy === "pdf" ? "PDF lädt…" : "PDF"}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Aufträge" value={s.anzahl_auftraege} testid="stat-auftraege" />
              <Stat label="Arbeitstage" value={s.arbeitstage} testid="stat-tage" />
              <Stat label="Verdienst pro Mitarbeiter" value={`€ ${fmt(s.gesamtsumme)}`} testid="stat-summe" />
              <Stat label="Status" value="Berechnet" />
            </div>
          </div>

          {/* Auftragsliste */}
          <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
            <div className="px-5 py-4 border-b" style={{ borderColor: "#E2E8F0" }}>
              <h3 className="font-bold">Erledigte Aufträge ({report.orders.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3">Datum</th>
                    <th className="text-left px-4 py-3">Auftragsnr.</th>
                    <th className="text-left px-4 py-3">KLS-ID</th>
                    <th className="text-left px-4 py-3">Typ</th>
                    <th className="text-left px-4 py-3">PL</th>
                    <th className="text-right px-4 py-3">Summe</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.orders.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">Keine Aufträge in diesem Zeitraum</td></tr>}
                  {report.orders.map((o) => (
                    <tr key={o.auftragsnummer} className="border-t" style={{ borderColor: "#E2E8F0" }} data-testid={`report-order-${o.auftragsnummer}`}>
                      <td className="px-4 py-2.5 tabular-nums">{o.datum}</td>
                      <td className="px-4 py-2.5 font-semibold">{o.auftragsnummer}</td>
                      <td className="px-4 py-2.5 mono">{o.kls_id}</td>
                      <td className="px-4 py-2.5">{o.auftragstyp}</td>
                      <td className="px-4 py-2.5 font-semibold">P{o.preisliste}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">€ {fmt(o.summe)}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={o.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Aufmaß Aggregat */}
          <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
            <div className="px-5 py-4 border-b" style={{ borderColor: "#E2E8F0" }}>
              <h3 className="font-bold">Gesamtes Aufmaß ({report.aufmass_aggregate.length} Positionen)</h3>
              <p className="text-xs text-slate-500 mt-0.5">Gruppiert nach Position · Einzelpreise aus der jeweiligen Preisliste pro Auftrag</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3">Pos.-Nr.</th>
                    <th className="text-left px-4 py-3">Beschreibung</th>
                    <th className="text-left px-4 py-3">Einheit</th>
                    <th className="text-right px-4 py-3">Gesamtmenge</th>
                    <th className="text-right px-4 py-3">Einzelpreis</th>
                    <th className="text-right px-4 py-3">Gesamtsumme</th>
                  </tr>
                </thead>
                <tbody>
                  {report.aufmass_aggregate.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">Keine Aufmaßdaten</td></tr>}
                  {report.aufmass_aggregate.map((a, i) => (
                    <tr key={i} className="border-t" style={{ borderColor: "#E2E8F0" }} data-testid={`report-pos-${a.pos_nummer}`}>
                      <td className="px-4 py-2.5 mono font-semibold">{a.pos_nummer}</td>
                      <td className="px-4 py-2.5">{a.beschreibung}</td>
                      <td className="px-4 py-2.5">{a.einheit}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmt(a.gesamtmenge)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">€ {fmt(a.einzelpreis)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">€ {fmt(a.gesamtsumme)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 bg-slate-50" style={{ borderColor: "#0A2540" }}>
                    <td colSpan={5} className="px-4 py-3 text-right font-bold uppercase tracking-wider text-xs text-slate-500">Gesamtsumme</td>
                    <td className="px-4 py-3 text-right tabular-nums text-lg font-bold" data-testid="report-grand-total">€ {fmt(s.gesamtsumme)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      <ActionBar onPrint={() => window.print()} onExport={report ? exportPdf : undefined} hideNext />
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-slate-50 border rounded-md p-4" style={{ borderColor: "#E2E8F0" }} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
    </div>
  );
}
